import { Fragment, useRef, useState, type ReactNode } from "react";
import { dayLabel } from "../../lib/format";
import { useInView } from "../../lib/hooks";
import { MessageSkeleton } from "../../components/ui/Skeleton";
import { Spinner } from "../../components/ui/Spinner";
import { IconArrowUp } from "../../components/icons";
import { MessageItem } from "./MessageItem";
import { messageKey } from "./cache";
import type { ChatMessage, MessageActions } from "./types";

const GROUP_WINDOW = 7 * 60 * 1000;

function sameDay(a: string, b: string) {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

/**
 * Bottom-anchored message list. A column-reverse scroller keeps the newest
 * message in view as content loads, and lets older history load above.
 */
export function MessageList({
  messages,
  loading,
  hasMore,
  loadingMore,
  onLoadMore,
  intro,
  meId,
  meUsername,
  actions,
  footer,
}: {
  messages: ChatMessage[];
  loading: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  intro: ReactNode;
  meId: string;
  meUsername: string;
  actions: MessageActions;
  footer?: ReactNode;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [away, setAway] = useState(false);
  const sentinel = useInView<HTMLDivElement>(() => hasMore && !loadingMore && onLoadMore(), hasMore, "400px");

  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={scrollRef}
        className="flex h-full flex-col-reverse overflow-y-auto overscroll-contain"
        onScroll={(e) => setAway(e.currentTarget.scrollTop < -600)}
        data-swipe-ignore
      >
        <div className="pb-4">
          {loading ? (
            <div className="pt-6">
              {Array.from({ length: 6 }, (_, i) => (
                <MessageSkeleton key={i} lines={(i % 3) + 1} />
              ))}
            </div>
          ) : (
            <>
              <div ref={sentinel} />
              {hasMore ? (
                <div className="flex h-16 items-center justify-center text-fg-3">{loadingMore && <Spinner className="size-4" />}</div>
              ) : (
                intro
              )}
              {messages.map((m, i) => {
                const prev = messages[i - 1];
                const newDay = !prev || !sameDay(prev.createdAt, m.createdAt);
                const grouped =
                  !!prev &&
                  !newDay &&
                  prev.author.id === m.author.id &&
                  prev.kind !== "system" &&
                  m.kind !== "system" &&
                  !m.replyTo &&
                  !m.interaction &&
                  new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < GROUP_WINDOW;
                return (
                  <Fragment key={messageKey(m)}>
                    {newDay && (
                      <div className="relative mx-4 mb-1 mt-5 flex items-center justify-center" role="separator">
                        <span className="absolute inset-x-0 top-1/2 h-px bg-line" />
                        <span className="relative bg-bg px-2 text-xs font-medium text-fg-3">{dayLabel(m.createdAt)}</span>
                      </div>
                    )}
                    <MessageItem msg={m} grouped={grouped} meId={meId} meUsername={meUsername} actions={actions} />
                  </Fragment>
                );
              })}
              {footer}
            </>
          )}
        </div>
      </div>
      {away && (
        <button
          onClick={() => scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" })}
          className="absolute bottom-3 left-1/2 flex h-8 -translate-x-1/2 items-center gap-1.5 rounded-full bg-surface-3 px-3 text-sm font-medium text-fg shadow-pop animate-slide-up"
        >
          <IconArrowUp className="icon size-4 rotate-180" aria-hidden />
          Jump to latest
        </button>
      )}
    </div>
  );
}
